import { ShieldCheck, Gauge } from 'lucide-react';
import Tooltip from '../ui/Tooltip';
import AiModelPicker from './AiModelPicker';
import { setSettingsModalOpen, setSettingsInitialTab } from '../../store/uiModalStore';
import { CONTEXT_WARN_TOKENS } from './aiChatConstants';
import { formatTokenCount } from './chatInputHelpers';

/**
 * Status strip under the composer: conversation context size + the model the
 * next turn will use. The monthly-quota meter that used to sit here died with
 * the server-metered proxy — the user's own provider account is the quota now
 * (BYO key).
 */
export function AiChatStatusBar({ contextTokens, whatIsSentText, t }) {
  const contextHintText = contextTokens >= CONTEXT_WARN_TOKENS
    ? 'This conversation is large — clear it (/clear or the reset button) to cut cost and latency.'
    : 'Tokens in the current conversation context (grows as the chat gets longer)';

  return (
    <div className="ai-chat-usage-bar ai-chat-usage-bar--footer">
      <Tooltip side="top" content={<div className="discord-tooltip-body">{contextHintText}</div>}>
        {/* aria-label as well as the tooltip, for the same reason as the
            chip beside it: a tooltip is a hover surface, and this chip is
            focusable. Without it the label is just "12.4k context". */}
        <span className="ai-chat-usage-item" tabIndex={0} aria-label={contextHintText}>
          <Gauge size={11} className="ai-chat-usage-icon" />
          <span className={`ai-chat-usage-num${contextTokens >= CONTEXT_WARN_TOKENS ? ' ai-chat-usage-num--warn' : ''}`}>
            {formatTokenCount(contextTokens)}
          </span>
          <span className="ai-chat-usage-label">context</span>
        </span>
      </Tooltip>
      {/* What the assistant can transmit, available at every point in the
          conversation rather than only on the empty state. GDPR Art. 13
          transparency is a standing obligation, and the one-time notice
          disappeared after the first message. */}
      <Tooltip side="top" content={<div className="discord-tooltip-body">{whatIsSentText}</div>}>
        {/* The same words on the element itself: a tooltip is a hover
            surface, and this disclosure has to reach a screen reader too. */}
        <span
          className="ai-chat-usage-item ai-chat-privacy-hint"
          tabIndex={0}
          aria-label={whatIsSentText}
          data-testid="ai-privacy-hint"
        >
          <ShieldCheck size={11} className="ai-chat-usage-icon" />
          <span className="ai-chat-usage-label">{t('aiChat.whatIsSent.label')}</span>
        </span>
      </Tooltip>
      {/* Model switcher — takes effect on the next turn (runAiTurn re-reads
          provider/model each turn), so it can be changed mid-conversation. */}
      <AiModelPicker
        onOpenSettings={() => {
          setSettingsInitialTab('aiChat');
          setSettingsModalOpen(true);
        }}
      />
    </div>
  );
}
